import React from "react";
import styled, { css } from "styled-components";
import * as _ from "lodash";

import Graph from "src/library/components/Graph";
import { OrderOrigin } from "src/core/orders/api/orders.api";
import {
  OrderTotalByOriginResult,
  useWidgetDataApi
} from "src/core/dashboard/api/widget-data.api";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { GraphPayload } from "src/library/components/Graph/GraphToolTip";
import { useWidgetContainerSize } from "src/core/dashboard/hooks";
import { WidgetDisplayTypes } from "src/core/dashboard/api/dashboard.api";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import {
  getDisplayRange,
  getGraphMetrics,
  DisplayRangeType,
  getSelectedWidgetView,
  WidgetProps
} from "../utils";
import DataLoadError from "../DataLoadError";
import { groupOrderTotalsByOrigin } from "./utils";
import { NumberWidget } from "../NumberWidget";
import { GraphDataType } from "../WidgetTotalValueSwitcher";
import { WidgetInnerHeader } from "../WidgetInnerHeader";
import { useWidgetFontSize } from "../hooks";

// Type for the flat object used to display data in graph
export interface OrderTotalByOriginGraphValue extends Omit<GraphPayload, "date"> {
  /** xAxisBy date */
  date?: Date;
  [OrderOrigin.File]?: number;
  [OrderOrigin.Prediction]?: number;
  [OrderOrigin.Manual]?: number;
  [OrderOrigin.StandingOrder]?: number;
  [OrderOrigin.DuplicateOrder]?: number;
  [OrderOrigin.List]?: number;
  [OrderOrigin.API]?: number;
  [OrderOrigin.Reminder]?: number;
  /** xAxisBy origin */
  origin?: OrderOrigin;
  value?: number;
}

const ORDER_TOTALS_BY_ORIGIN_CACHE_KEY = "widget-order-totals-by-origin";

/**
 * Returns order totals by origin widget content
 */
export const OrderTotalsByOriginWidget: React.FunctionComponent<WidgetProps> = ({
  options, filters, widgetId, ignoreCache = false, widgetTypeId
}) => {
  const [ displayRange, setDisplayRange ] = React.useState<DisplayRangeType>("day");
  const [ ordersByOriginDataResponse, ordersByOriginDataRequest ] = useWidgetDataApi("GET_ORDER_TOTALS_BY_ORIGIN");
  const [ xAxisBy, setXAisBy ] = React.useState<"date" | "origin">("date");
  const [ graphDataType, setGraphDataType ] = React.useState<GraphDataType>("value");
  const wrapperRef = React.createRef<HTMLDivElement>();
  const { width } = useWidgetContainerSize(wrapperRef);
  const fontSize = useWidgetFontSize(width, options.view);

  const selectedView = React.useMemo(() => {
    return getSelectedWidgetView(options.view);
  }, [ options ]);

  // On load, get the data we need
  React.useEffect(() => {
    ordersByOriginDataRequest({
      widgetTypeId,
      filters,
      options: null
    }, {
      cache: {
        maxAge: cacheMinutesToMilliseconds(60),
        key: () => `${ORDER_TOTALS_BY_ORIGIN_CACHE_KEY}-${widgetTypeId}`,
        ignoreCache
      }
    });
  }, [
    filters,
    ignoreCache,
    ordersByOriginDataRequest,
    widgetTypeId
  ]);

  // Takes the raw response from API and transforms the result to the shape required for graph data.
  // During this process, we also sets the displayRange & xAxisBy state
  const transformToGraphData = React.useCallback((orderTotalsByOriginResult: OrderTotalByOriginResult): OrderTotalByOriginGraphValue[] => {
    // Find range to determine whether to display in days, weeks or months
    const calculatedDisplayRange = getDisplayRange(orderTotalsByOriginResult);

    // Update state required by Graph component
    setDisplayRange(calculatedDisplayRange);
    setXAisBy(calculatedDisplayRange === "day" ? "origin" : "date");

    // Group, accumulate totals and return
    return groupOrderTotalsByOrigin(orderTotalsByOriginResult, xAxisBy, calculatedDisplayRange, graphDataType);
  }, [ xAxisBy, graphDataType ]);

  // The data used to display metrics
  const graphDisplayData = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      if (ordersByOriginDataResponse.data?.result) {
        return transformToGraphData(ordersByOriginDataResponse.data?.result);
      }
    }

    return null;
  }, [
    ordersByOriginDataResponse.data?.result,
    selectedView,
    transformToGraphData
  ]);

  const graphMetrics = React.useMemo(() => {
    if (selectedView === "line" || selectedView === "bar") {
      return graphDisplayData ? getGraphMetrics(graphDisplayData, [ xAxisBy ]) : [];
    }

    return null;
  }, [
    graphDisplayData,
    selectedView,
    xAxisBy
  ]);

  // The data used to display number metrics
  const numberDisplayData = React.useMemo(() => {
    if (selectedView === WidgetDisplayTypes.Number) {
      const orderTotalByOriginResult = ordersByOriginDataResponse.data?.result;

      if (orderTotalByOriginResult) {
        const orderTotalsGroupedByType = groupOrderTotalsByOrigin(orderTotalByOriginResult, "origin") as Array<{ origin: string, value: number }>;

        return {
          value: orderTotalByOriginResult.reduce((value, order) => value + order.value, 0),
          total: orderTotalByOriginResult.reduce((total, order) => total + order.count, 0),
          data: orderTotalsGroupedByType.map(typeData => {
            return {
              group: _.startCase(typeData.origin.replace(/[_-]/g, " ")),
              value: typeData.value,
              total: orderTotalByOriginResult.reduce((total, order) => {
                if (typeData.origin === order.origin) {
                  return total + order.count;
                }

                return total;
              }, 0)
            };
          })
        };
      }
    }

    return null;
  }, [ ordersByOriginDataResponse.data?.result, selectedView ]);

  if (selectedView !== "bar" && selectedView !== "line" && selectedView !== WidgetDisplayTypes.Number) {
    // Currently, graph bar and line are the only handled view options availble below
    return null;
  }

  return (
    <StyledWrapper ref={wrapperRef}>
      {ordersByOriginDataResponse.pending && (
        <LoadingSpinner type="circular" />
      )}

      {graphDisplayData && graphMetrics && !ordersByOriginDataResponse.error && (selectedView === "line" || selectedView === "bar") && (
        <>
          <WidgetInnerHeader
            title="Order Totals"
            containerWidth={width}
            graphDataType={graphDataType}
            updateGraphDataType={setGraphDataType}
          />

          <Graph
            containerWidth={width}
            hideLegend={xAxisBy === "origin"}
            stacked={xAxisBy === "date"}
            data={graphDisplayData}
            type={selectedView}
            toolTipValueType={graphDataType === "value" ? "price" : "number"}
            graphMetrics={graphMetrics}
            xAxis={{
              dataKey: xAxisBy,
              dateFormat: displayRange === "month" ? "month" : "day"
            }}
          />
        </>
      )}

      {numberDisplayData && fontSize && !ordersByOriginDataResponse.error && (
        <NumberWidget
          widgetId={widgetId}
          displayData={numberDisplayData}
          valueType="price"
          units="orders"
          fontSize={fontSize}
        />
      )}

      {ordersByOriginDataResponse.error && (
        <DataLoadError errorMessage={ordersByOriginDataResponse.error.message} />
      )}

    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    height: 100%;
    width: 100%;
    padding: ${theme.spacing.padding.sm} 0; 
  `}
`;
